import React, { useState, useEffect, useCallback } from 'react';
import { crmAPI } from '../../lib/api';
import { useCurrency } from '../../lib/CurrencyContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { ScrollArea } from '../ui/scroll-area';
import { Skeleton } from '../ui/skeleton';
import { Progress } from '../ui/progress';
import { PageFilters, YearFilter, QuarterFilter } from '../layout/PageFilters';
import { CompactBusinessRules } from '../layout/CompactBusinessRules';
import { DataFreshnessBar } from '../layout/DataFreshnessBar';
import {
  ShoppingCart, Search, TrendingUp, RefreshCw, ChevronUp, ChevronDown,
  DollarSign, CheckCircle, Clock, AlertTriangle, Package, Eye, RotateCcw,
  ArrowRight, BarChart2, Target, Users, ExternalLink, MessageSquare, Download
} from 'lucide-react';
import { toast } from 'sonner';
import { SOChatterLogs } from './SOChatterLogs';

const BILLING_STYLES = {
  fully_invoiced:     { bg: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  partially_invoiced: { bg: 'bg-amber-100 text-amber-700',    dot: 'bg-amber-500' },
  not_invoiced:       { bg: 'bg-slate-100 text-slate-700',    dot: 'bg-slate-500' },
};
const billStyle = (s) => BILLING_STYLES[(s||'').toLowerCase()] || BILLING_STYLES.not_invoiced;
const fmtPct = (n) => `${(n || 0).toFixed(1)}%`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const FUNNEL_COLORS = {
  'Won Opportunities': { bg: 'bg-purple-100', text: 'text-purple-700', bar: 'bg-purple-400', icon: Target },
  'SO Created':        { bg: 'bg-blue-100',   text: 'text-blue-700',   bar: 'bg-blue-400',   icon: ShoppingCart },
  'Partially Invoiced':{ bg: 'bg-amber-100',  text: 'text-amber-700',  bar: 'bg-amber-400',  icon: Clock },
  'Fully Invoiced':    { bg: 'bg-emerald-100',text: 'text-emerald-700',bar: 'bg-emerald-500',icon: CheckCircle },
};

export default function SalesOrdersPage() {
  const { formatCurrency, currency } = useCurrency();
  const [displayMode, setDisplayMode] = useState('compact');
  const formatCurrencyCompact = (value) => {
    const num = Number(value || 0);
    if (Math.abs(num) >= 1000000) return `OMR ${(num / 1000000).toFixed(2)}M`;
    if (Math.abs(num) >= 1000) return `OMR ${(num / 1000).toFixed(1)}K`;
    return formatCurrency(num, currency);
  };
  const money = (value) => displayMode === 'compact' ? formatCurrencyCompact(value) : formatCurrency(value || 0, currency);
  const [orders, setOrders]         = useState([]);
  const [stats, setStats]           = useState(null);
  const [pipeline, setPipeline]     = useState(null);
  const [margins, setMargins]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [pipelineLoading, setPipelineLoading] = useState(true);
  const [search, setSearch]         = useState('');
  const [detail, setDetail]         = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [activeTab, setActiveTab]   = useState('overview');
  const [billingAlerts, setBillingAlerts] = useState(null);
  const [odooBaseUrl, setOdooBaseUrl]     = useState('');

  const exportOrders = async () => {
    try {
      toast.info('Generating Sales Orders Excel export...');
      const rows = filtered.map(order => ({
        'SO Number': order.so_number,
        Customer: order.customer,
        Opportunity: order.opportunity_name,
        Salesperson: order.salesperson,
        'Created By': order.created_by,
        'Order Date': order.order_date,
        'Amount Total': order.amount_total,
        Margin: order.margin,
        'Margin %': order.margin_percent,
        'Invoice Status': order.billing_label,
        'Raw Odoo Invoice Status': order.invoice_status_raw || '',
        'Custom Invoicing Status': order.custom_invoicing_status || '',
      }));
      const res = await crmAPI.exportSalesOrdersExcel({ file_name: `sales_orders_${filters.year !== 'all' ? filters.year : 'all'}.xlsx`, rows });
      const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales_orders_${filters.year !== 'all' ? filters.year : 'all'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Sales Orders Excel export downloaded');
    } catch (error) {
      console.error(error);
      toast.error('Failed to export Sales Orders');
    }
  };

  const [filters, setFilters] = useState({ year: 'all', quarter: 'all', status: '', salesperson: '' });
  const [sortBy, setSortBy]       = useState('order_date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Load Odoo config once on mount for deeplinks
  useEffect(() => {
    crmAPI.getSalesOrdersOdooConfig().then(r => setOdooBaseUrl(r.data?.odoo_base_url || '')).catch(() => {});
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setPipelineLoading(true);
    try {
      const yearParam = filters.year && filters.year !== 'all' ? filters.year : undefined;
      const quarterParam = filters.quarter && filters.quarter !== 'all' ? filters.quarter : undefined;
      const sharedParams = {};
      if (quarterParam) sharedParams.quarter = quarterParam;
      if (filters.salesperson) sharedParams.salesperson = filters.salesperson;
      if (filters.status) sharedParams.status = filters.status;
      const listParams = { sort_by: sortBy, sort_order: sortOrder, limit: 500, ...(yearParam ? { year: yearParam } : {}), ...sharedParams };

      const [ordersRes, statsRes, pipelineRes, marginRes, alertsRes] = await Promise.allSettled([
        crmAPI.listSalesOrders(listParams),
        crmAPI.getSalesOrdersStats({ ...(yearParam ? { year: yearParam } : {}), ...sharedParams }),
        crmAPI.getSalesOrdersPipeline({ ...(yearParam ? { year: yearParam } : {}), ...sharedParams }),
        crmAPI.getSalesOrdersMarginAnalysis({ ...(yearParam ? { year: yearParam } : {}), ...sharedParams }),
        crmAPI.getSalesOrdersBillingAlerts({ ...(yearParam ? { year: yearParam } : {}), ...sharedParams, days_threshold: 30 }),
      ]);
      if (ordersRes.status === 'fulfilled') setOrders(ordersRes.value.data?.orders || []);
      if (statsRes.status === 'fulfilled')  setStats(statsRes.value.data);
      if (pipelineRes.status === 'fulfilled') setPipeline(pipelineRes.value.data);
      if (marginRes.status === 'fulfilled')   setMargins(marginRes.value.data);
      if (alertsRes.status === 'fulfilled')   setBillingAlerts(alertsRes.value.data);
    } catch { toast.error('Failed to load sales orders'); }
    finally { setLoading(false); setPipelineLoading(false); }
  }, [filters, sortBy, sortOrder]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSort = (col) => {
    if (sortBy === col) setSortOrder(o => o === 'desc' ? 'asc' : 'desc');
    else { setSortBy(col); setSortOrder('desc'); }
  };
  const SortIcon = ({ col }) =>
    sortBy !== col ? null : sortOrder === 'desc'
      ? <ChevronDown className="h-3 w-3 inline ml-1" />
      : <ChevronUp className="h-3 w-3 inline ml-1" />;

  const openDetail = async (order) => {
    try {
      const res = await crmAPI.getSalesOrder(order.id);
      setDetail(res.data);
      setDetailOpen(true);
    } catch { toast.error('Failed to load SO details'); }
  };

  const filtered = orders.filter(o => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (o.so_number||'').toLowerCase().includes(q) ||
           (o.customer||'').toLowerCase().includes(q) ||
           (o.salesperson||'').toLowerCase().includes(q) ||
           (o.created_by||'').toLowerCase().includes(q) ||
           (o.opportunity_name||'').toLowerCase().includes(q) ||
           (o.billing_label||'').toLowerCase().includes(q) ||
           (o.custom_invoicing_status||'').toLowerCase().includes(q);
  });

  const salespersons = stats?.filter_options?.salespersons || [...new Set(orders.map(o => o.salesperson).filter(Boolean))].sort();

  // Odoo deeplink builder
  const odooLink = (model, id) => id && odooBaseUrl
    ? `${odooBaseUrl}/web#id=${id}&model=${model}&view_type=form`
    : null;

  return (
    <div className="space-y-6 p-6" data-testid="sales-orders-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <ShoppingCart className="h-6 w-6 text-[#800000]" />
            Sales Orders
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">Track SO progress, billing status, and margins</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={displayMode} onValueChange={setDisplayMode}>
            <SelectTrigger className="w-32 h-9" data-testid="so-display-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="compact">Compact</SelectItem>
              <SelectItem value="exact">Exact</SelectItem>
            </SelectContent>
          </Select>
          <CompactBusinessRules
            scopeItems={[filters.year !== 'all' ? filters.year : null, filters.quarter !== 'all' ? filters.quarter : null, filters.status, filters.salesperson].filter(Boolean)}
            ruleKeys={['invoice_progress', 'totals_scope']}
            label="Rules"
            dataTestId="sales-orders-compact-business-rules"
          />
          <Button variant="outline" size="sm" className="h-9" onClick={exportOrders} data-testid="so-export-btn">
            <Download className="h-4 w-4 mr-1" /> Export Excel
          </Button>
          <Button variant="outline" size="sm" className="h-9" onClick={loadData} data-testid="so-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      <PageFilters
        onReset={() => { setFilters({ year: 'all', quarter: 'all', status: '', salesperson: '' }); setSearch(''); }}
        activeFilters={[filters.year !== 'all' ? filters.year : null, filters.quarter !== 'all' ? filters.quarter : null, filters.status, filters.salesperson]}
        title="Sales Order Filters"
      >
        <YearFilter value={filters.year === 'all' ? null : filters.year} onChange={(v) => setFilters(f => ({ ...f, year: v || 'all' }))} years={stats?.filter_options?.years || ['2026','2025','2024']} />
        <QuarterFilter value={filters.quarter === 'all' ? null : filters.quarter} onChange={(v) => setFilters(f => ({ ...f, quarter: v || 'all' }))} />
        <Select value={filters.salesperson || 'all'} onValueChange={v => setFilters(f => ({ ...f, salesperson: v === 'all' ? '' : v }))}>
          <SelectTrigger className="w-44 h-9"><SelectValue placeholder="Salesperson" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Salespersons</SelectItem>{salespersons.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </PageFilters>

      <DataFreshnessBar entityIds={['sales_orders', 'so_lines', 'so_log_messages']} actionEntityIds={['sales_orders']} title="Sales Order Data Freshness" dataTestId="sales-orders-data-freshness" />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {loading ? [...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />) : [
          { label: 'Total SO Value', value: money(stats?.total_value||0), sub: `${stats?.total_orders||0} orders`, icon: ShoppingCart, c: 'text-[#800000]', bg: 'bg-[#800000]/10' },
          { label: 'Total Margin',   value: money(stats?.total_margin||0), sub: `${fmtPct(stats?.avg_margin_pct)} avg`, icon: TrendingUp, c: 'text-emerald-600', bg: 'bg-emerald-100' },
          { label: 'Fully Invoiced', value: stats?.fully_invoiced||0, sub: money(stats?.by_status?.fully_invoiced?.amount||0), icon: CheckCircle, c: 'text-emerald-600', bg: 'bg-emerald-100' },
          { label: 'Partially Invoiced', value: stats?.partially_invoiced||0, sub: money(stats?.by_status?.partially_invoiced?.amount||0), icon: Clock, c: 'text-amber-600', bg: 'bg-amber-100' },
          { label: 'Not Invoiced', value: stats?.not_invoiced||0, sub: money(stats?.by_status?.not_invoiced?.amount||0), icon: AlertTriangle, c: 'text-slate-600', bg: 'bg-slate-100' },
        ].map(kpi => { const I=kpi.icon; return (
          <Card key={kpi.label} className="h-full"><CardContent className="pt-5 min-h-[132px] flex flex-col justify-between">
            <div className="flex items-start justify-between gap-3 min-w-0">
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground leading-snug">{kpi.label}</p>
                <p className={`mt-2 font-bold leading-tight tracking-tight ${kpi.c} text-[clamp(1.5rem,2.1vw,2.7rem)] break-words`}>
                  {kpi.value}
                </p>
              </div>
              <div className={`p-2.5 rounded-2xl shrink-0 ${kpi.bg}`}><I className={`h-5 w-5 ${kpi.c}`} /></div>
            </div>
            <p className="text-xs text-muted-foreground mt-3 leading-snug">{kpi.sub}</p>
          </CardContent></Card>
        );})}
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="so-tab-overview">
            <BarChart2 className="h-3.5 w-3.5 mr-1.5" />Overview
          </TabsTrigger>
          <TabsTrigger value="orders" data-testid="so-tab-orders">
            <ShoppingCart className="h-3.5 w-3.5 mr-1.5" />Orders List ({stats?.total_orders||0})
          </TabsTrigger>
          <TabsTrigger value="alerts" data-testid="so-tab-alerts">
            <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />
            Billing Alerts
            {(billingAlerts?.overdue_count || 0) > 0 && (
              <span className="ml-1.5 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">{billingAlerts.overdue_count}</span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ── OVERVIEW TAB: Pipeline Funnel + Margin Analysis ── */}
        <TabsContent value="overview" className="mt-4 space-y-6">

          {/* Win-to-Revenue Funnel */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Target className="h-4 w-4 text-[#800000]" /> Win-to-Revenue Pipeline
                <span className="ml-auto text-xs font-normal text-muted-foreground">
                  End-to-end conversion: <span className="font-bold text-[#800000]">{fmtPct(pipeline?.conversion_rates?.end_to_end)}</span>
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pipelineLoading ? <Skeleton className="h-32" /> : (
                <>
                  {/* Funnel steps */}
                  <div className="flex items-center gap-0 overflow-x-auto pb-2" data-testid="so-pipeline-funnel">
                    {(pipeline?.funnel||[]).map((stage, i) => {
                      const c = FUNNEL_COLORS[stage.stage] || { bg:'bg-gray-100', text:'text-gray-700', bar:'bg-gray-400', icon: ShoppingCart };
                      const Icon = c.icon;
                      const isLast = i === (pipeline.funnel.length - 1);
                      return (
                        <React.Fragment key={stage.stage}>
                          <div className={`flex-1 min-w-36 p-3 rounded-lg ${c.bg} text-center`} data-testid={`funnel-stage-${i}`}>
                            <Icon className={`h-4 w-4 mx-auto mb-1 ${c.text}`} />
                            <p className={`text-xs font-semibold ${c.text}`}>{stage.stage}</p>
                            <p className={`text-2xl font-bold ${c.text} mt-1`}>{stage.count}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{formatCurrency(stage.value, currency)}</p>
                          </div>
                          {!isLast && (
                            <div className="flex flex-col items-center px-1.5 shrink-0">
                              <ArrowRight className="h-4 w-4 text-muted-foreground" />
                              <span className="text-[10px] text-muted-foreground font-medium mt-0.5">{fmtPct(stage.pct === 100 ? pipeline?.conversion_rates?.won_to_so : (pipeline?.funnel?.[i+1]?.pct))}</span>
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>

                  {/* Monthly trend bar chart */}
                  {pipeline?.monthly_trend?.length > 0 && (
                    <div className="mt-4">
                      <p className="text-xs font-semibold text-muted-foreground mb-2">Monthly SO Volume</p>
                      <div className="flex items-end gap-1 h-20">
                        {pipeline.monthly_trend.map((m, i) => {
                          const maxVal = Math.max(...pipeline.monthly_trend.map(x => x.so_value));
                          const h = maxVal > 0 ? Math.max(4, Math.round(m.so_value / maxVal * 72)) : 4;
                          const hI = maxVal > 0 ? Math.max(2, Math.round(m.invoiced_value / maxVal * 72)) : 2;
                          return (
                            <div key={m.month} className="flex-1 flex flex-col items-center gap-0.5" title={`${m.month}: ${m.so_count} SOs | OMR ${Math.round(m.so_value).toLocaleString()}`}>
                              <div className="w-full relative rounded-sm overflow-hidden bg-blue-100" style={{ height: h }}>
                                <div className="absolute bottom-0 left-0 right-0 bg-emerald-400 rounded-sm" style={{ height: hI }} />
                              </div>
                              <span className="text-[9px] text-muted-foreground">{m.month.slice(5)}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="flex items-center gap-1 text-[10px] text-muted-foreground"><span className="w-2 h-2 rounded-sm bg-blue-200 inline-block" />SO Total</span>
                        <span className="flex items-center gap-1 text-[10px] text-muted-foreground"><span className="w-2 h-2 rounded-sm bg-emerald-400 inline-block" />Invoiced</span>
                      </div>
                    </div>
                  )}

                  {/* Billing Velocity KPI */}
                  {billingAlerts?.billing_velocity && (
                    <div className="mt-4 p-3 rounded-lg bg-muted/40 flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground">Billing Velocity</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Avg days from SO creation to first invoice</p>
                      </div>
                      <div className="flex items-center gap-4 text-right">
                        <div>
                          <p className={`text-2xl font-bold ${(billingAlerts.billing_velocity.avg_days||99) <= 30 ? 'text-emerald-600' : (billingAlerts.billing_velocity.avg_days||99) <= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                            {billingAlerts.billing_velocity.avg_days != null ? `${billingAlerts.billing_velocity.avg_days}d` : '—'}
                          </p>
                          <p className="text-[10px] text-muted-foreground">avg ({billingAlerts.billing_velocity.sample_size} SOs)</p>
                        </div>
                        {billingAlerts.overdue_count > 0 && (
                          <div className="text-right">
                            <p className="text-2xl font-bold text-red-600">{billingAlerts.overdue_count}</p>
                            <p className="text-[10px] text-muted-foreground">overdue 30d+</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Margin Analysis */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* By Salesperson */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2"><Users className="h-4 w-4 text-[#800000]" />Margin by Salesperson</CardTitle>
              </CardHeader>
              <CardContent>
                {pipelineLoading ? <Skeleton className="h-40" /> : (margins?.by_salesperson||[]).slice(0,8).map((sp, i) => {
                  const maxV = (margins?.by_salesperson||[])[0]?.value || 1;
                  const pct = Math.round(sp.value / maxV * 100);
                  return (
                    <div key={sp.name} className="flex items-center gap-2 mb-2" data-testid={`margin-sp-${i}`}>
                      <span className="text-xs text-muted-foreground w-4">{i+1}</span>
                      <span className="text-xs flex-1 truncate font-medium">{sp.name}</span>
                      <div className="w-24 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-[#800000]" style={{ width: `${pct}%` }} />
                      </div>
                      <span className={`text-xs font-semibold w-10 text-right ${sp.margin_pct>=50?'text-emerald-600':sp.margin_pct>=30?'text-amber-600':'text-red-600'}`}>{fmtPct(sp.margin_pct)}</span>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Invoice Progress Mix */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2"><BarChart2 className="h-4 w-4 text-[#800000]" />Invoice Progress Mix</CardTitle>
              </CardHeader>
              <CardContent>
                {pipelineLoading ? <Skeleton className="h-40" /> : Object.entries(stats?.by_status || {}).map(([statusKey, data]) => {
                  const totalValue = Math.max(...Object.values(stats?.by_status || {}).map(item => item.amount || 0), 1);
                  const pct = Math.round((data.amount || 0) / totalValue * 100);
                  const tone = billStyle(statusKey);
                  return (
                    <div key={statusKey} className="mb-3">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-medium truncate flex-1">{data.label}</span>
                        <span className="text-muted-foreground ml-2">{formatCurrency(data.amount, currency)}</span>
                        <span className={`ml-2 font-semibold ${tone.bg.replace('bg-', 'text-').replace('-100', '-700')}`}>{data.count}</span>
                      </div>
                      <Progress value={pct} className="h-1.5" />
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* By Product Category */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2"><Package className="h-4 w-4 text-[#800000]" />Margin by Product Category</CardTitle>
              </CardHeader>
              <CardContent>
                {pipelineLoading ? <Skeleton className="h-40" /> : (margins?.by_category||[]).slice(0,8).map((c, i) => {
                  const maxV = (margins?.by_category||[])[0]?.value || 1;
                  const pct = Math.round(c.value / maxV * 100);
                  return (
                    <div key={c.name} className="flex items-center gap-2 mb-2">
                      <span className="text-xs flex-1 truncate">{c.name}</span>
                      <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-blue-400" style={{ width: `${pct}%` }} />
                      </div>
                      <span className={`text-xs font-semibold w-10 text-right ${c.margin_pct>=50?'text-emerald-600':c.margin_pct>=30?'text-amber-600':'text-red-600'}`}>{fmtPct(c.margin_pct)}</span>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* By Product Director */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2"><DollarSign className="h-4 w-4 text-[#800000]" />Margin by Product Director</CardTitle>
              </CardHeader>
              <CardContent>
                {pipelineLoading ? <Skeleton className="h-40" /> : (margins?.by_product_manager||[]).slice(0,8).map((pm, i) => {
                  const maxV = (margins?.by_product_manager||[])[0]?.value || 1;
                  const pct = Math.round(pm.value / maxV * 100);
                  return (
                    <div key={pm.name} className="flex items-center gap-2 mb-2">
                      <span className="text-xs flex-1 truncate">{pm.name}</span>
                      <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-purple-400" style={{ width: `${pct}%` }} />
                      </div>
                      <span className={`text-xs font-semibold w-10 text-right ${pm.margin_pct>=50?'text-emerald-600':pm.margin_pct>=30?'text-amber-600':'text-red-600'}`}>{fmtPct(pm.margin_pct)}</span>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── ORDERS TAB: filterable table ── */}
        <TabsContent value="orders" className="mt-4 space-y-4">

          {/* Billing Status Pills */}
          {!loading && stats?.by_status && (
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.by_status).map(([st, d]) => {
                const bs = billStyle(st);
                return (
                  <button key={st} data-testid={`so-status-filter-${st.replace(' ','-')}`}
                    onClick={() => setFilters(f => ({ ...f, status: f.status === st ? '' : st }))}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${filters.status === st ? `${bs.bg} ring-2 ring-offset-1 ring-current` : 'bg-background border-border text-muted-foreground hover:bg-muted'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${bs.dot}`} />
                    {d.label || st} · {d.count}
                  </button>
                );
              })}
            </div>
          )}

          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-48 max-w-80">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search SO#, customer, salesperson..." className="pl-9 h-9" data-testid="so-search-input" />
            </div>
            <Select value={filters.salesperson||'all'} onValueChange={v => setFilters(f => ({...f, salesperson: v==='all'?'':v}))}>
              <SelectTrigger className="w-44 h-9"><SelectValue placeholder="Salesperson" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Salespersons</SelectItem>{salespersons.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            {(filters.status||filters.salesperson||search) && (
              <Button variant="ghost" size="sm" className="h-9 text-muted-foreground" onClick={() => { setFilters(f=>({...f,status:'',salesperson:''})); setSearch(''); }}>
                <RotateCcw className="h-3 w-3 mr-1" /> Reset
              </Button>
            )}
            <span className="ml-auto text-xs text-muted-foreground">{filtered.length} orders</span>
          </div>

          {/* Orders Table */}
          <Card>
            <Table>
              <TableHeader>
                <TableRow className="text-xs">
                  <TableHead className="cursor-pointer hover:text-foreground" onClick={() => handleSort('so_number')}>SO # <SortIcon col="so_number" /></TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Opportunity</TableHead>
                  <TableHead className="cursor-pointer hover:text-foreground text-right" onClick={() => handleSort('amount_total')}>Amount <SortIcon col="amount_total" /></TableHead>
                  <TableHead className="cursor-pointer hover:text-foreground text-right" onClick={() => handleSort('margin')}>Margin <SortIcon col="margin" /></TableHead>
                  <TableHead>Margin %</TableHead>
                  <TableHead>Salesperson</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead>Billing</TableHead>
                  <TableHead className="cursor-pointer hover:text-foreground" onClick={() => handleSort('order_date')}>Date <SortIcon col="order_date" /></TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? [...Array(8)].map((_,i)=><TableRow key={i}>{[...Array(11)].map((__,j)=><TableCell key={j}><Skeleton className="h-4 w-full"/></TableCell>)}</TableRow>)
                : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={11} className="text-center py-12 text-muted-foreground">
                    <ShoppingCart className="h-8 w-8 mx-auto mb-2 opacity-30" /><p>No sales orders found</p>
                  </TableCell></TableRow>
                ) : filtered.map((order, i) => {
                  const bs = billStyle(order.invoice_status);
                  return (
                    <TableRow key={order.id||i} className="hover:bg-muted/30 text-sm" data-testid={`so-row-${order.so_number}`}>
                      <TableCell className="font-mono font-semibold text-[#800000]">{order.so_number}</TableCell>
                      <TableCell className="max-w-36 truncate" title={order.customer}>{order.customer||'—'}</TableCell>
                      <TableCell className="max-w-40 truncate text-muted-foreground text-xs" title={order.opportunity_name}>{order.opportunity_name||'—'}</TableCell>
                      <TableCell className="font-mono font-semibold text-right">{formatCurrency(order.amount_total, currency)}</TableCell>
                      <TableCell className="font-mono text-emerald-700 text-right">{formatCurrency(order.margin, currency)}</TableCell>
                      <TableCell><span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${order.margin_percent>=40?'text-emerald-700 bg-emerald-50':order.margin_percent>=20?'text-amber-700 bg-amber-50':'text-red-700 bg-red-50'}`}>{fmtPct(order.margin_percent)}</span></TableCell>
                      <TableCell className="text-xs">{order.salesperson||'—'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{order.created_by||'—'}</TableCell>
                      <TableCell><Badge className={`text-xs ${bs.bg} border-0`} data-testid={`so-billing-${order.so_number}`}><span className={`w-1.5 h-1.5 rounded-full ${bs.dot} mr-1 inline-block`}/>{order.billing_label}</Badge></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{fmtDate(order.order_date)}</TableCell>
                      <TableCell><Button variant="ghost" size="icon" className="h-7 w-7" onClick={()=>openDetail(order)} data-testid={`so-detail-btn-${order.so_number}`}><Eye className="h-3.5 w-3.5"/></Button>
                      {odooBaseUrl && order.source_record_id && (
                        <a href={odooLink('sale.order', order.source_record_id)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center h-7 w-7 text-muted-foreground hover:text-[#800000]" title="Open in Odoo" data-testid={`odoo-link-${order.so_number}`}>
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* ── BILLING ALERTS TAB ── */}
        <TabsContent value="alerts" className="mt-4 space-y-4">
          {/* Velocity + Alert Summary */}
          <div className="grid grid-cols-3 gap-4">
            <Card className={`${(billingAlerts?.overdue_count||0) > 0 ? 'border-red-200' : ''}`}>
              <CardContent className="pt-5">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-100"><AlertTriangle className="h-5 w-5 text-red-600" /></div>
                  <div>
                    <p className="text-xs text-muted-foreground">Overdue Billing (30d+)</p>
                    <p className="text-2xl font-bold text-red-600">{billingAlerts?.overdue_count || 0}</p>
                    <p className="text-xs text-muted-foreground">{formatCurrency(billingAlerts?.overdue_value||0, currency)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100"><Clock className="h-5 w-5 text-blue-600" /></div>
                  <div>
                    <p className="text-xs text-muted-foreground">Avg Billing Velocity</p>
                    <p className={`text-2xl font-bold ${(billingAlerts?.billing_velocity?.avg_days||99)<=30?'text-emerald-600':(billingAlerts?.billing_velocity?.avg_days||99)<=60?'text-amber-600':'text-red-600'}`}>
                      {billingAlerts?.billing_velocity?.avg_days != null ? `${billingAlerts.billing_velocity.avg_days}d` : '—'}
                    </p>
                    <p className="text-xs text-muted-foreground">SO → first invoice</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-100"><Target className="h-5 w-5 text-amber-600" /></div>
                  <div>
                    <p className="text-xs text-muted-foreground">Median Velocity</p>
                    <p className="text-2xl font-bold text-amber-600">
                      {billingAlerts?.billing_velocity?.median_days != null ? `${billingAlerts.billing_velocity.median_days}d` : '—'}
                    </p>
                    <p className="text-xs text-muted-foreground">{billingAlerts?.billing_velocity?.sample_size || 0} invoiced SOs</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Overdue Billing Table */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-500" />
                SOs Not Fully Invoiced for 30+ Days
                <span className="ml-auto text-xs font-normal text-muted-foreground">{billingAlerts?.overdue_count || 0} orders</span>
              </CardTitle>
            </CardHeader>
            <Table>
              <TableHeader>
                <TableRow className="text-xs">
                  <TableHead>SO #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Opportunity</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Invoice Status</TableHead>
                  <TableHead>Salesperson</TableHead>
                  <TableHead>Order Date</TableHead>
                  <TableHead className="text-right">Days Pending</TableHead>
                  {odooBaseUrl && <TableHead className="w-10" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {!billingAlerts ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-6"><Skeleton className="h-4 w-32 mx-auto" /></TableCell></TableRow>
                ) : (billingAlerts.overdue_billing||[]).length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                    <CheckCircle className="h-8 w-8 mx-auto mb-2 text-emerald-400 opacity-60" />
                    <p className="text-sm">All SOs are being invoiced promptly!</p>
                  </TableCell></TableRow>
                ) : (billingAlerts.overdue_billing||[]).map((so, i) => {
                  const urgent = so.days_pending >= 60;
                  const link = odooLink('sale.order', so.source_record_id);
                  return (
                    <TableRow key={so.so_number||i} className="text-sm" data-testid={`alert-row-${so.so_number}`}>
                      <TableCell className="font-mono font-semibold text-[#800000]">{so.so_number}</TableCell>
                      <TableCell className="max-w-36 truncate">{so.customer||'—'}</TableCell>
                      <TableCell className="max-w-36 truncate text-muted-foreground text-xs">{so.opportunity_name||'—'}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(so.amount_total, currency)}</TableCell>
                      <TableCell><Badge className={`text-xs border-0 ${billStyle(so.invoice_status).bg}`}>{so.billing_label}</Badge></TableCell>
                      <TableCell className="text-xs">{so.salesperson||'—'}</TableCell>
                      <TableCell className="text-xs">{fmtDate(so.order_dt||so.order_date)}</TableCell>
                      <TableCell className="text-right">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${urgent?'bg-red-100 text-red-700':'bg-amber-100 text-amber-700'}`}>
                          {so.days_pending}d
                        </span>
                      </TableCell>
                      {odooBaseUrl && (
                        <TableCell>
                          {link && (
                            <a href={link} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-[#800000]" title="Open in Odoo" data-testid={`odoo-link-${so.so_number}`}>
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* SO Detail Sheet */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent className="w-full sm:max-w-xl">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <ShoppingCart className="h-4 w-4 text-[#800000]" />{detail?.so_number}
              {detail?.source_record_id && odooBaseUrl && (
                <a href={`${odooBaseUrl}/web#id=${detail.source_record_id}&model=sale.order&view_type=form`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-[#800000]" title="Open in Odoo">
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </SheetTitle>
          </SheetHeader>
          {detail && (
            <div className="mt-3 h-[calc(100vh-80px)] flex flex-col">
              {/* Detail tabs: Overview / Logs */}
              <Tabs defaultValue="overview" className="flex flex-col flex-1 min-h-0">
                <TabsList className="shrink-0 w-full">
                  <TabsTrigger value="overview" className="flex-1">Overview</TabsTrigger>
                  <TabsTrigger value="logs" className="flex-1" data-testid="so-detail-logs-tab">
                    <MessageSquare className="h-3.5 w-3.5 mr-1" />Logs
                  </TabsTrigger>
                </TabsList>

                {/* ── OVERVIEW ── */}
                <TabsContent value="overview" className="flex-1 overflow-hidden">
                <ScrollArea className="h-full">
              <div className="space-y-4 pr-2 py-2">
                {/* Core Info Grid */}
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: 'Customer', value: detail.customer },
                    { label: 'Salesperson', value: detail.salesperson },
                    { label: 'Created By', value: detail.created_by||'—' },
                    { label: 'Order Date', value: fmtDate(detail.order_date) },
                    { label: 'Customer Ref / PO', value: detail.customer_ref||'—' },
                    { label: 'Delivery Date', value: fmtDate(detail.commitment_date) },
                    { label: 'Purchase Orders', value: detail.purchase_order_count ?? '—' },
                  ].map(f=>(
                    <div key={f.label} className="p-2 rounded-lg bg-muted/40">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{f.label}</p>
                      <p className="text-xs font-medium mt-0.5 truncate" title={String(f.value)}>{f.value}</p>
                    </div>
                  ))}
                </div>

                {/* Payment Terms — highlighted if present */}
                {(detail.payment_terms || detail.customer_payment_term) && (
                  <div className={`p-3 rounded-lg border ${(detail.payment_terms||detail.customer_payment_term||'').toLowerCase().includes('advance') ? 'border-amber-300 bg-amber-50' : 'border-blue-200 bg-blue-50'}`}>
                    <p className="text-[10px] font-bold uppercase tracking-wide mb-1 flex items-center gap-1 text-blue-700">
                      <DollarSign className="h-3 w-3" /> Payment Terms
                    </p>
                    {detail.payment_terms && (
                      <p className="text-xs font-semibold">{detail.payment_terms}</p>
                    )}
                    {detail.customer_payment_term && detail.customer_payment_term !== detail.payment_terms && (
                      <p className="text-xs text-muted-foreground mt-0.5">{detail.customer_payment_term}</p>
                    )}
                    {detail.advance_payment > 0 && (
                      <p className="text-[10px] text-amber-700 mt-0.5">Advance required: {fmtPct(detail.advance_payment)}</p>
                    )}
                  </div>
                )}
                {!detail.payment_terms && !detail.customer_payment_term && (
                  <div className="p-3 rounded-lg border border-red-200 bg-red-50 flex items-center gap-2">
                    <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                    <p className="text-xs text-red-700 font-medium">No payment terms recorded — verify with customer before invoicing</p>
                  </div>
                )}

                {/* Customer Delivery Terms */}
                {detail.customer_delivery_term && (
                  <div className="p-2 rounded-lg bg-muted/40">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Customer Delivery Terms</p>
                    <p className="text-xs font-medium mt-0.5">{detail.customer_delivery_term}</p>
                  </div>
                )}

                {/* Contract Dates */}
                {(detail.contract_start_date || detail.contract_end_date) && (
                  <div className="grid grid-cols-2 gap-2">
                    {detail.contract_start_date && (
                      <div className="p-2 rounded-lg bg-muted/40">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Contract Start</p>
                        <p className="text-xs font-medium mt-0.5">{fmtDate(detail.contract_start_date)}</p>
                      </div>
                    )}
                    {detail.contract_end_date && (
                      <div className="p-2 rounded-lg bg-muted/40">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Contract End</p>
                        <p className="text-xs font-medium mt-0.5">{fmtDate(detail.contract_end_date)}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Approval Team */}
                {(detail.sales_hod || detail.presales_user || detail.account_assistant || detail.project) && (
                  <div className="p-3 rounded-lg bg-muted/20 border">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold mb-2">Approval Team</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {detail.sales_hod && <div className="text-xs"><span className="text-muted-foreground">Sales HOD: </span>{detail.sales_hod}</div>}
                      {detail.presales_user && <div className="text-xs"><span className="text-muted-foreground">Pre-sales: </span>{detail.presales_user}</div>}
                      {detail.account_assistant && <div className="text-xs"><span className="text-muted-foreground">Account: </span>{detail.account_assistant}</div>}
                      {detail.project && <div className="text-xs"><span className="text-muted-foreground">Project: </span>{detail.project}</div>}
                    </div>
                  </div>
                )}

                {/* Financials */}
                <div className="grid grid-cols-3 gap-3">
                  <Card className="border-0 bg-muted/30"><CardContent className="pt-3 pb-2">
                    <p className="text-[10px] text-muted-foreground">Amount (excl. tax)</p>
                    <p className="font-bold">{formatCurrency(detail.amount, currency)}</p>
                  </CardContent></Card>
                  <Card className="border-0 bg-emerald-50"><CardContent className="pt-3 pb-2">
                    <p className="text-[10px] text-muted-foreground">Margin</p>
                    <p className="font-bold text-emerald-700">{formatCurrency(detail.margin, currency)}</p>
                    <p className="text-[10px] text-emerald-600">{fmtPct(detail.margin_percent)}</p>
                  </CardContent></Card>
                  <Card className="border-0 bg-muted/20"><CardContent className="pt-3 pb-2">
                    <p className="text-[10px] text-muted-foreground">Billing</p>
                    <Badge className={`text-xs mt-1 ${billStyle(detail.invoice_status).bg} border-0`}>{detail.billing_label}</Badge>
                  </CardContent></Card>
                </div>

                {detail.opportunity_name && (
                  <div className="p-3 rounded-lg border border-[#800000]/20 bg-[#800000]/5">
                    <p className="text-[10px] text-[#800000] uppercase tracking-wide font-semibold mb-1">Linked Opportunity</p>
                    <p className="text-sm font-medium">{detail.opportunity_name}</p>
                  </div>
                )}

                {/* Terms & Conditions Note */}
                {detail.note && (
                  <div className="p-3 rounded-lg bg-muted/20 border">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold mb-1">Terms & Conditions Note</p>
                    <p className="text-xs text-muted-foreground" dangerouslySetInnerHTML={{ __html: detail.note }} />
                  </div>
                )}
                {detail.lines?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold mb-2 flex items-center gap-1.5"><Package className="h-3.5 w-3.5"/>Order Lines ({detail.lines.length})</p>
                    <div className="space-y-2">
                      {detail.lines.map((line, i) => (
                        <div key={i} className="p-3 rounded-lg border bg-muted/20 text-xs">
                          <div className="flex justify-between">
                            <span className="font-medium truncate mr-2">{(line.product_name&&!String(line.product_name).startsWith("[\'"))?line.product_name:(line.description||line.product_name||'Product')}</span>
                            <span className="font-mono font-semibold whitespace-nowrap">{formatCurrency(line.subtotal, currency)}</span>
                          </div>
                          <div className="flex gap-4 mt-1 text-muted-foreground">
                            <span>Qty: {line.quantity}</span>
                            <span>Unit: {formatCurrency(line.unit_price, currency)}</span>
                            {line.line_margin_percent > 0 && <span className="text-emerald-600">Margin: {fmtPct(line.line_margin_percent < 2 ? line.line_margin_percent * 100 : line.line_margin_percent)}</span>}
                          </div>
                          {line.product_manager && <div className="mt-1 text-[10px] text-muted-foreground">PD: {line.product_manager}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
                </ScrollArea>
                </TabsContent>

                {/* ── LOGS TAB ── */}
                <TabsContent value="logs" className="flex-1 overflow-hidden">
                  <ScrollArea className="h-full">
                    <div className="pr-2 py-2">
                      <SOChatterLogs soId={detail.id} soNumber={detail.so_number} />
                    </div>
                  </ScrollArea>
                </TabsContent>

              </Tabs>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
